import { useCallback, useState } from 'react'
import { api } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { tenantConfig } from '@/lib/tenant'
import { useAuthStore, type AuthUser, type UserRole } from './store'

export interface AuthContext {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isAdmin: boolean
  login: (username: string, role: UserRole) => Promise<void>
  logout: () => void | Promise<void>
}

export function useDevAuth(): AuthContext {
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const setAuth = useAuthStore((s) => s.setAuth)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const login = useCallback(
    async (username: string, role: UserRole) => {
      const res = await api.devLogin({ username, role })
      const rawUser = res.user as Record<string, unknown>
      const authUser: AuthUser = {
        username: String(rawUser.username ?? username),
        role: (rawUser.role as UserRole) ?? role,
        deployment_id: rawUser.deployment_id as string | undefined,
        brand_ids: rawUser.brand_ids as string[] | undefined,
      }
      setAuth(res.token, authUser)
    },
    [setAuth],
  )

  const logout = useCallback(() => {
    clearAuth()
  }, [clearAuth])

  return {
    user,
    token,
    isAuthenticated: !!token,
    isLoading: false,
    isAdmin: user?.role === 'admin',
    login,
    logout,
  }
}

export function DevSignInPanel() {
  const { login } = useDevAuth()
  const [username, setUsername] = useState('demo-user')
  const [role, setRole] = useState<UserRole>('admin')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(username, role)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Sign in to {tenantConfig.displayName}</CardTitle>
        <CardDescription>
          Development login for the attribution control plane POC.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium">
              Username
            </label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="demo-user"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="role" className="text-sm font-medium">
              Role
            </label>
            <Select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </Select>
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
